import { alpha, outlinedInputClasses, type SxProps, type Theme } from '@mui/material';
import { DROPDOWN_OPTION_PADDING_X } from '@onevision-resources/design-theme';
import { getModalListFrameSx } from './modal-container.styles';

export const CREATE_ROW_ATTRIBUTE = 'data-create-row';

export const LOAD_MORE_ROW_ATTRIBUTE = 'data-load-more-row';

export const FOLDER_ROW_ATTRIBUTE = 'data-folder';

export const FOLDER_OPEN_ATTRIBUTE = 'data-folder-open';

export const NESTED_ROW_ATTRIBUTE = 'data-nested';

export const NESTED_END_ATTRIBUTE = 'data-nested-end';

export const ROW_LEVEL_VARIABLE = '--modal-list-row-level';

const FOLDER_RADIUS = '0.625rem';

// The nested rows sit on a lighter wash of the folder's own tone, the way the design fuses an
// open folder and its contents into one block.
const NESTED_WASH_OPACITY = 0.45;

const getFolderSx = (theme: Theme) => {
  const { surfaceContainerHigh, surfaceContainerHighest } = theme.palette.custom.OV.sys;
  return {
    [`& [cmdk-item][${FOLDER_ROW_ATTRIBUTE}]`]: {
      marginTop: theme.spacing(0.75),
      borderRadius: FOLDER_RADIUS,
      backgroundColor: surfaceContainerHigh,
    },
    [`& [cmdk-item][${FOLDER_ROW_ATTRIBUTE}][data-selected="true"]`]: {
      backgroundColor: surfaceContainerHighest,
    },
    [`& [cmdk-item][${FOLDER_OPEN_ATTRIBUTE}]`]: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    [`& [cmdk-item][${NESTED_ROW_ATTRIBUTE}]`]: {
      borderRadius: 0,
      paddingLeft: `calc(${DROPDOWN_OPTION_PADDING_X} + var(${ROW_LEVEL_VARIABLE}) * 2rem)`,
      backgroundColor: alpha(surfaceContainerHigh, NESTED_WASH_OPACITY),
    },
    [`& [cmdk-item][${NESTED_ROW_ATTRIBUTE}][data-selected="true"]`]: {
      backgroundColor: surfaceContainerHigh,
    },
    [`& [cmdk-item][${NESTED_END_ATTRIBUTE}]`]: {
      borderBottomLeftRadius: FOLDER_RADIUS,
      borderBottomRightRadius: FOLDER_RADIUS,
    },
  };
};

// With a pinned create row only the rows scroll; the divider and the row stay under them.
// Own selector strings: spread into the root sx, a repeated key would replace the frame's list rules.
const pinnedCreateRowSx = {
  // The modal body's bottom padding reads as dead space under a button-like row; keep a little.
  marginBottom: -1.5,
  '& [cmdk-root] [cmdk-list]': {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  '& [cmdk-list] [cmdk-list-sizer]': {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  [`& [cmdk-list] [${CREATE_ROW_ATTRIBUTE}]`]: {
    flexShrink: 0,
  },
};

export const pinnedRowsScrollSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
};

export const pinnedCreateDividerSx: SxProps<Theme> = {
  flexShrink: 0,
  mt: 0.5,
  mb: 0.75,
  borderTop: '1px solid',
  borderColor: 'custom.OV.sys.outlineVariant',
};

// Rows, highlight and group captions come from SelectMenu's CommandRoot; only the frame differs:
// a fixed height, no inset (rows line up with the filter), captions on the modal surface.
interface ModalListRootOptions {
  fullScreen: boolean;
  readOnly: boolean;
  pinnedCreateRow: boolean;
  pinnedFirstRow: boolean;
}

export const getModalListRootSx =
  ({
    fullScreen,
    readOnly,
    pinnedCreateRow,
    pinnedFirstRow,
  }: ModalListRootOptions): SxProps<Theme> =>
  (theme) => ({
    ...getModalListFrameSx(theme, fullScreen),
    '& [cmdk-root]': { flex: '1 1 auto' },
    '& [cmdk-list]': { maxHeight: 'none', padding: 0, marginTop: theme.spacing(1.25) },
    // One plain line under the filter with the Create row right below it, not pushed to the bottom.
    '& [cmdk-empty] > *, & [cmdk-loading] > *': {
      justifyContent: 'flex-start',
      padding: '1.25rem 0.25rem 0.875rem',
    },
    '& [cmdk-group-heading]': {
      top: 0,
      backgroundColor: theme.palette.custom.OV.sys.modalSurface,
    },
    [`& [${CREATE_ROW_ATTRIBUTE}], & [${CREATE_ROW_ATTRIBUTE}] *, & [${LOAD_MORE_ROW_ATTRIBUTE}], & [${LOAD_MORE_ROW_ATTRIBUTE}] *`]:
      {
        color: theme.palette.custom.OV.sys.secondary,
      },
    ...getFolderSx(theme),
    // iOS Safari zooms the page into a focused field whose text is under 16px.
    [theme.breakpoints.down('sm')]: {
      [`& .${outlinedInputClasses.root}`]: { fontSize: '1rem' },
    },
    ...(readOnly && {
      '& [cmdk-item][data-disabled="true"]': { opacity: 1, cursor: 'default' },
    }),
    ...(pinnedCreateRow && pinnedCreateRowSx),
    // A pinnedFirst row moves up through CSS order, which needs its rows in a flex column; only
    // lists that use it get one, since flex items keep margins other lists rely on collapsing.
    ...(pinnedFirstRow && {
      '& [cmdk-list-sizer]': { display: 'flex', flexDirection: 'column' },
    }),
  });

export const filterAdornmentSx: SxProps<Theme> = {
  color: 'custom.OV.sys.onSurfaceVariant',
  '& svg': { width: '0.875rem', height: '0.875rem' },
};

export const getFolderChevronSx =
  (expanded: boolean): SxProps<Theme> =>
  (theme) => ({
    display: 'flex',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    transform: expanded ? 'rotate(180deg)' : 'none',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '& svg': { width: '0.875rem', height: '0.875rem' },
  });

export const titleEditorSx: SxProps<Theme> = { display: 'flex', flex: 1, minWidth: 0 };

export const rowActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};
